import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { HistoryPanel } from '../history/HistoryPanel';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { leaveGuarded, sameValues, useUnsavedGuard } from '../ui/unsaved';
import { DeleteRoleDialog, RoleFormDialog, ROLES_KEY } from './RoleDialogs';
import { actionLabel } from './labels';
import { defaultFieldVisible, fieldKey, scopeLabel, SensitiveBadge } from './permissionUi';
import { buildFieldEntries } from './rolePayload';
import type { CatalogueEntry, FieldEntry, PermissionEntry, Role, Scope } from './types';
import { Notice } from '../ui/Notice';

type RoleData = { role: Role; permissions: PermissionEntry[]; fields: FieldEntry[]; catalogue: CatalogueEntry[] };
type Grant = Scope | 'none';

const permKey = (resource: string, action: string) => `${resource}\u0000${action}`;

function initialGrants(data: RoleData): Record<string, Grant> {
  const grants: Record<string, Grant> = {};
  for (const entry of data.catalogue) for (const action of entry.actions) grants[permKey(entry.resource, action)] = 'none';
  for (const p of data.permissions) grants[permKey(p.resource, p.action)] = p.scope;
  return grants;
}

function initialFields(data: RoleData): Record<string, boolean> {
  const fields: Record<string, boolean> = {};
  for (const entry of data.catalogue) for (const f of entry.fields) fields[fieldKey(entry.resource, f.field)] = defaultFieldVisible(f.sensitive);
  for (const f of data.fields) fields[fieldKey(f.resource, f.field)] = f.canView;
  return fields;
}

function Matrix({ data, onConflict }: { data: RoleData; onConflict: () => void }) {
  const queryClient = useQueryClient();
  const [grants, setGrants] = useState(() => initialGrants(data));
  const [fields, setFields] = useState(() => initialFields(data));
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const permissions: PermissionEntry[] = [];
      for (const entry of data.catalogue)
        for (const action of entry.actions) {
          const grant = grants[permKey(entry.resource, action)];
          if (grant && grant !== 'none') permissions.push({ resource: entry.resource, action, scope: grant });
        }
      const fieldEntries = buildFieldEntries(data.catalogue, fields);
      return api<RoleData>(`/api/admin/roles/${data.role.id}/permissions`, {
        method: 'PUT',
        body: { version: data.role.version, permissions, fields: fieldEntries },
      });
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(['admin', 'role-permissions', data.role.id], fresh);
      void queryClient.invalidateQueries({ queryKey: ROLES_KEY }); // the role's version moved on
      setSaved(true);
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  // owner 2026-10-23 21:14: another role, another screen or "Wstecz" with unsaved permissions asks first
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ grants, fields }, { grants: initialGrants(data), fields: initialFields(data) }),
    save: async () => {
      await save.mutateAsync();
      return true;
    },
  });

  return (
    <form
      ref={guardRef}
      className="matrix"
      data-feature-id="identity.roles.permissions"
      onSubmit={(e) => {
        e.preventDefault();
        setSaved(false);
        save.mutate();
      }}
    >
      {conflict && <ConflictNotice onReload={onConflict} />}
      {save.error && !conflict && (
        <p className="alert" role="alert">
          {save.error instanceof ApiError ? save.error.message : 'Nie udało się zapisać uprawnień'}
        </p>
      )}
      {saved && (
        <Notice onClose={() => setSaved(false)}>
          Zapisano uprawnienia roli „{data.role.label}”.
        </Notice>
      )}
      {data.catalogue.map((entry) => (
        <fieldset key={entry.resource} className="panel resource">
          <legend>{entry.label}</legend>
          <ul className="perm-list">
            {entry.actions.map((action) => {
              const id = `perm-${entry.resource}-${action}`;
              return (
                <li key={action}>
                  <label htmlFor={id}>{actionLabel(entry, action)}</label>
                  <select
                    id={id}
                    value={grants[permKey(entry.resource, action)] ?? 'none'}
                    onChange={(e) => setGrants({ ...grants, [permKey(entry.resource, action)]: e.target.value as Grant })}
                  >
                    <option value="none">Brak dostępu</option>
                    {entry.scopes.map((scope) => (
                      <option key={scope} value={scope}>
                        {entry.scopes.length === 1 ? 'Dozwolone' : scopeLabel[scope]}
                      </option>
                    ))}
                  </select>
                </li>
              );
            })}
          </ul>
          {entry.fields.length > 0 && (
            <div className="field-block">
              <h3>Widoczne pola</h3>
              <ul className="perm-list">
                {entry.fields.map((f) => (
                  <li key={f.field} data-field={f.field} className={f.sensitive ? 'sensitive-row' : undefined}>
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={fields[fieldKey(entry.resource, f.field)] ?? false}
                        onChange={(e) => setFields({ ...fields, [fieldKey(entry.resource, f.field)]: e.target.checked })}
                      />
                      <span>{f.label}</span>
                    </label>
                    {f.sensitive && <SensitiveBadge />}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </fieldset>
      ))}
      <div className="sticky-actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz uprawnienia'}
        </button>
      </div>
    </form>
  );
}

export function RolePermissionsPage() {
  const queryClient = useQueryClient();
  const roles = useQuery({
    queryKey: ROLES_KEY,
    queryFn: () => api<{ roles: Role[] }>('/api/admin/roles').then((r) => r.roles),
  });
  const [roleId, setRoleId] = useState('');
  // Bumped after a conflict reload so the form restarts from the server's state.
  const [epoch, setEpoch] = useState(0);
  const [dialog, setDialog] = useState<'create' | 'rename' | 'delete' | null>(null);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    // first role at the start, and again when the chosen one is gone (deleted)
    if (roles.data?.[0] && !roles.data.some((r) => r.id === roleId)) setRoleId(roles.data[0].id);
  }, [roles.data, roleId]);
  const current = roles.data?.find((r) => r.id === roleId);

  const key = ['admin', 'role-permissions', roleId];
  const data = useQuery({
    queryKey: key,
    queryFn: () => api<RoleData>(`/api/admin/roles/${roleId}/permissions`),
    enabled: Boolean(roleId),
  });

  return (
    <section className="page" data-feature-id="identity.roles.permissions">
      <div className="page-head">
        <h1>Uprawnienia ról</h1>
        <button className="btn primary" type="button" data-feature-id="identity.roles.manage" onClick={() => setDialog('create')}>
          Dodaj rolę
        </button>
      </div>
      <p className="muted lede">
        Wybierz rolę i ustal, co jej osoby mogą robić w każdej sekcji. Zakres „Tylko własne” oznacza rekordy przypisane do danej osoby.
        Pola wrażliwe są ukryte, dopóki ich nie odblokujesz.
      </p>
      {notice && (
        <Notice onClose={() => setNotice('')}>
          {notice}
        </Notice>
      )}
      <div className="perm-picker-row">
        <label className="field perm-picker">
          <span>Rola</span>
          <select
            value={roleId}
            onChange={(e) => {
              const next = e.target.value;
              leaveGuarded(() => {
                setNotice('');
                setRoleId(next);
              });
            }}
          >
            {roles.data?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        {current && (
          <RowMenu
            label={`Więcej: ${current.label}`}
            featureId="identity.roles.manage"
            actions={[
              { label: 'Zmień nazwę', onSelect: () => setDialog('rename') },
              ...(current.name === 'wlasciciel' ? [] : [{ label: 'Usuń rolę', danger: true, onSelect: () => setDialog('delete') }]),
            ]}
          />
        )}
      </div>
      {current?.name === 'wlasciciel' && (
        <p className="note" role="note">
          Pełne uprawnienia daje oznaczenie konta jako właściciel; ta rola służy do porządku na liście.
        </p>
      )}
      {data.error && (
        <p className="alert" role="alert">
          {data.error instanceof ApiError ? data.error.message : 'Nie udało się wczytać uprawnień'}
        </p>
      )}
      {data.data && (
        <Matrix
          key={`${data.data.role.id}:${epoch}`}
          data={data.data}
          onConflict={() => void queryClient.invalidateQueries({ queryKey: key }).then(() => setEpoch((n) => n + 1))}
        />
      )}
      {current && <HistoryPanel entityId={current.id} resource="identity.roles" />}
      {dialog === 'create' && (
        <RoleFormDialog
          roles={roles.data ?? []}
          onClose={() => setDialog(null)}
          onSaved={(role) => {
            setDialog(null);
            setRoleId(role.id);
            setNotice(`Dodano rolę „${role.label}”. Ustaw jej uprawnienia poniżej.`);
          }}
        />
      )}
      {dialog === 'rename' && current && (
        <RoleFormDialog
          roles={roles.data ?? []}
          role={current}
          onClose={() => setDialog(null)}
          onSaved={(role) => {
            setDialog(null);
            setNotice(`Zmieniono nazwę roli na „${role.label}”.`);
            void queryClient.invalidateQueries({ queryKey: key });
          }}
        />
      )}
      {dialog === 'delete' && current && (
        <DeleteRoleDialog
          role={current}
          onClose={() => setDialog(null)}
          onDeleted={() => {
            setDialog(null);
            setNotice(`Usunięto rolę „${current.label}”.`);
          }}
        />
      )}
    </section>
  );
}
